import React, { useEffect, useState } from "react";
import API from "../services/api";
import "./PageBuilder.css";

import CreateTagSectionForm from "../components/pagebuilder/CreateTagSectionForm";
import CreatePageForm from "../components/pagebuilder/CreatePageForm";
import ExistingPagesList from "../components/pagebuilder/ExistingPagesList";
import ExistingSectionsList from "../components/pagebuilder/ExistingSectionsList";

const PageBuilder = ({ theme }) => {
  // Lists
  const [pages, setPages] = useState([]);
  const [sections, setSections] = useState([]);
  const [tags, setTags] = useState([]);
  const [pageSections, setPageSections] = useState([]);
  const [sectionTags, setSectionTags] = useState([]);

  // Create Section
  const [sectionTitle, setSectionTitle] = useState("");
  const [sectionDesc, setSectionDesc] = useState("");
  const [sectionMode, setSectionMode] = useState("analog"); // analog | digital | io
  const [newSectionTagIds, setNewSectionTagIds] = useState([]);

  // Create Page
  const [pageTitle, setPageTitle] = useState("");
  const [pageDesc, setPageDesc] = useState("");
  const [newPageSectionIds, setNewPageSectionIds] = useState([]);

  const [message, setMessage] = useState("");

  const fetchAll = async () => {
    try {
      const [pRes, sRes, tRes, psRes, stRes] = await Promise.all([
        API.get("/pages/"),
        API.get("/tag-sections/"),
        API.get("/tags/"),
        API.get("/page-sections/"),
        API.get("/tag-section-tags/"),
      ]);
      setPages(pRes.data || []);
      setSections(sRes.data || []);
      setTags(tRes.data || []);
      setPageSections(psRes.data || []);
      setSectionTags(stRes.data || []);
    } catch (e) {
      console.error(e);
      setMessage("Failed to load data from API.");
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // Create Section
  const createSection = async (e) => {
    e.preventDefault();
    setMessage("");
    try {
      const response = await API.post("/tag-sections/", {
        title: sectionTitle,
        description: sectionDesc,
        mode: sectionMode,
      });
      await Promise.all(newSectionTagIds.map((tagId, order) => API.post("/tag-section-tags/", {
        section: response.data.id,
        tag: Number(tagId),
        order,
      })));
      setSectionTitle("");
      setSectionDesc("");
      setSectionMode("analog");
      setNewSectionTagIds([]);
      setMessage(newSectionTagIds.length ? `Section created with ${newSectionTagIds.length} tags.` : "Section created.");
      await fetchAll();
    } catch (e) {
      console.error(e);
      setMessage("Failed to create section.");
    }
  };

  // Create Page
  const createPage = async (e) => {
    e.preventDefault();
    setMessage("");
    try {
      const response = await API.post("/pages/", {
        title: pageTitle,
        description: pageDesc,
      });
      await Promise.all(newPageSectionIds.map((sectionId, order) => API.post("/page-sections/", {
        page: response.data.id,
        section: Number(sectionId),
        order,
      })));
      setPageTitle("");
      setPageDesc("");
      setNewPageSectionIds([]);
      setMessage(newPageSectionIds.length ? `Page created with ${newPageSectionIds.length} sections.` : "Page created.");
      await fetchAll();
    } catch (e) {
      console.error(e);
      setMessage("Failed to create page.");
    }
  };

  const mutate = async (request, success, failure) => {
    setMessage("");
    try {
      await request();
      setMessage(success);
      await fetchAll();
      return true;
    } catch (error) {
      console.error(error);
      setMessage(failure);
      return false;
    }
  };

  const updatePage = async (id, data) => {
    const saved = await mutate(() => API.patch(`/pages/${id}/`, data), "Page updated.", "Failed to update page.");
    if (saved && Object.prototype.hasOwnProperty.call(data, "is_published")) window.dispatchEvent(new Event("aqueris-pages-changed"));
    return saved;
  };
  const deletePage = async (id) => {
    const removed = await mutate(() => API.delete(`/pages/${id}/`), "Page deleted.", "Failed to delete page.");
    if (removed) window.dispatchEvent(new Event("aqueris-pages-changed"));
    return removed;
  };
  const updateSection = (id, data) => mutate(() => API.patch(`/tag-sections/${id}/`, data), "Section updated.", "Failed to update section.");
  const deleteSection = (id) => mutate(() => API.delete(`/tag-sections/${id}/`), "Section deleted. Tags were preserved.", "Failed to delete section.");
  const createPageSection = (data) => mutate(() => API.post("/page-sections/", data), "Section added to page.", "Failed to add section to page.");
  const updatePageSection = (id, data) => mutate(() => API.patch(`/page-sections/${id}/`, data), "Page-section connection updated.", "Failed to update page-section connection.");
  const deletePageSection = (id) => mutate(() => API.delete(`/page-sections/${id}/`), "Page-section connection removed.", "Failed to remove page-section connection.");
  const createSectionTag = (data) => mutate(() => API.post("/tag-section-tags/", data), "Tag added to section.", "Failed to add tag to section.");
  const updateSectionTag = (id, data) => mutate(() => API.patch(`/tag-section-tags/${id}/`, data), "Section-tag connection updated.", "Failed to update section-tag connection.");
  const deleteSectionTag = (id) => mutate(() => API.delete(`/tag-section-tags/${id}/`), "Section-tag connection removed.", "Failed to remove section-tag connection.");

  return (
    <div className="pb-container" data-theme={theme}>
      <div className="pb-eyebrow">Configuration</div>
      <h1 className="pb-title">Page Builder</h1>
      <p className="pb-subtitle">
        Create Pages, Sections (Analog/Digital/IO) and attach sections to pages.
      </p>

      {message && <div className="pb-message">{message}</div>}

      <div className="pb-grid">
        <CreateTagSectionForm
          sectionTitle={sectionTitle}
          setSectionTitle={setSectionTitle}
          sectionDesc={sectionDesc}
          setSectionDesc={setSectionDesc}
          sectionMode={sectionMode}
          setSectionMode={setSectionMode}
          tags={tags}
          selectedTagIds={newSectionTagIds}
          setSelectedTagIds={setNewSectionTagIds}
          onSubmit={createSection}
        />

        <CreatePageForm
          pageTitle={pageTitle}
          setPageTitle={setPageTitle}
          pageDesc={pageDesc}
          setPageDesc={setPageDesc}
          sections={sections}
          selectedSectionIds={newPageSectionIds}
          setSelectedSectionIds={setNewPageSectionIds}
          onSubmit={createPage}
        />
      </div>

      <div className="pb-lists">
        <ExistingSectionsList
          sections={sections} tags={tags} sectionTags={sectionTags} setMessage={setMessage}
          onUpdate={updateSection} onDelete={deleteSection}
          onAddTag={createSectionTag} onUpdateTag={updateSectionTag} onDeleteTag={deleteSectionTag}
        />
        <ExistingPagesList
          pages={pages} sections={sections} pageSections={pageSections} setMessage={setMessage}
          onUpdate={updatePage} onDelete={deletePage}
          onAddSection={createPageSection} onUpdateSection={updatePageSection} onDeleteSection={deletePageSection}
        />
      </div>
    </div>
  );
};

export default PageBuilder;
